/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo, useState } from 'react';
import { Button, Radio, Select, Spin, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

type PreviewArgs = {
  project_id: number;
  tender_id: number;
  lang: string;
};

type PreviewMode = 'email' | 'modification_report';

export type PreviewTabProps = {
  projectId: number;
  tenderId: number;

  // Email list preview (existing)
  getPreview: (args: PreviewArgs) => Promise<string>;

  // Modification Report preview (new)
  getModificationReportPreview: (args: PreviewArgs) => Promise<string>;
  isInQaTab?: boolean;
  hideModeSelector?: boolean;
  previewId?: string | number;
};

export const PreviewTab: React.FC<PreviewTabProps> = ({
  projectId,
  tenderId,
  getPreview,
  isInQaTab,
  getModificationReportPreview,
  hideModeSelector,
  previewId,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // ==constants
  const initialLang = useMemo(() => {
    // If your intl locale is like "en" / "da" use it directly.
    // If it's "en-US" - cut to "en".
    const loc = (intl.locale || 'en').toLowerCase();
    const base = loc.split('-')[0];
    return ['da', 'en', 'de', 'sv'].includes(base) ? base : 'en';
  }, [intl.locale]);

  // ==hooks
  const [lang, setLang] = useState<string>(initialLang);

  const [mode, setMode] = useState<PreviewMode>('email');

  const modeOptions = useMemo(
    () => [
      {
        value: 'email',
        label: intl.formatMessage({ id: 'correction-letter-preview-email' }),
      },
      {
        value: 'modification_report',
        label: intl.formatMessage({
          id: 'correction-letter-preview-modification-report',
        }),
      },
    ],
    [intl]
  );

  const previewQueryKey = useMemo(
    () => [
      'correction_letter_preview_html',
      projectId,
      tenderId,
      lang,
      mode,
      previewId,
    ],
    [projectId, tenderId, lang, mode, previewId]
  );

  // ==query
  const previewQuery = useQuery<string>(
    previewQueryKey,
    () => {
      const args = { project_id: projectId, tender_id: tenderId, lang };
      if (mode === 'modification_report') {
        return getModificationReportPreview(args);
      }
      return getPreview(args);
    },
    {
      enabled: !!previewId || (!!projectId && !!tenderId),
      refetchOnWindowFocus: false,
    }
  );

  const emailOnlyOptions = useMemo(
    () => modeOptions.filter((option) => option.value === 'email'),
    [modeOptions]
  );

  // ==return
  return (
    <div css={styles.wrapper}>
      <div css={styles.topBar}>
        <div css={styles.leftBar}>
          <div css={styles.langBlock}>
            <Typography.Text type="secondary">
              {intl.formatMessage({ id: 'language' })}:
            </Typography.Text>
            <Radio.Group
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              buttonStyle="solid">
              <Radio.Button value="da">DA</Radio.Button>
              <Radio.Button value="en">EN</Radio.Button>
              <Radio.Button value="de">DE</Radio.Button>
              <Radio.Button value="sv">SV</Radio.Button>
            </Radio.Group>
          </div>
        </div>

        {/* centered selector */}
        {hideModeSelector ? (
          <div style={{ flex: '1 1 auto' }} />
        ) : (
          <div css={styles.modeBlock}>
            <Typography.Text type="secondary">
              {intl.formatMessage({ id: 'correction-letter-mode' })}:
            </Typography.Text>
            <Select
              value={mode}
              onChange={(v: PreviewMode) => setMode(v)}
              options={modeOptions}
              dropdownMatchSelectWidth={false}
              css={styles.modeSelect}
            />
          </div>
        )}

        <div css={styles.rightBar}>
          <Button
            onClick={() => previewQuery.refetch()}
            disabled={previewQuery.isFetching}>
            {intl.formatMessage({ id: 'refresh' })}
          </Button>
        </div>
      </div>

      <div css={styles.previewBox}>
        {previewQuery.isLoading ? (
          <div css={styles.centerBox}>
            <Spin />
            <Typography.Text type="secondary">
              {intl.formatMessage({ id: 'loading' })}
            </Typography.Text>
          </div>
        ) : previewQuery.error ? (
          <div css={styles.centerBox}>
            <Typography.Text type="danger">
              {intl.formatMessage({ id: 'correction-letter-preview-failed' })}
            </Typography.Text>
          </div>
        ) : (
          <iframe
            title="correction-letter-preview"
            css={styles.iframe}
            // srcDoc = safest + simplest way to show raw HTML in a preview
            srcDoc={
              previewQuery.data || '<div style="padding:16px;">(empty)</div>'
            }
            sandbox="allow-same-origin"
          />
        )}
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    paddingBottom: 18,
    paddingTop: 5,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  leftBar: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: '0 0 auto',
  },
  rightBar: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  langBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  modeBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    flex: '1 1 auto',
  },
  modeSelect: {
    minWidth: 230,
  },
  previewBox: {
    width: '100%',
    height: '58.5vh',
    borderRadius: 8,
    border: '1px solid #f0f0f0',
    overflow: 'hidden',
    background: '#fff',
    position: 'relative',
  },
  centerBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    background: '#fff',
  },
});
